import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Rating,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Movie,
  Tv,
  Star,
  PlayArrow
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { generateClient } from 'aws-amplify/api';
import { getUserWatchlist } from '../../graphql/queries';
import { getMovieById, getShowById } from '../../services/mongoApi';

// Simple query to get watchlist items
const GET_WATCHLIST_ITEMS = /* GraphQL */ `
  query WatchlistItemsByWatchlistIDAndCreatedAt(
    $watchlistID: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelWatchlistItemFilterInput
    $limit: Int
    $nextToken: String
  ) {
    watchlistItemsByWatchlistIDAndCreatedAt(
      watchlistID: $watchlistID
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        userID
        watchlistID
        movieID
        showID
        contentType
        status
        priority
        notes
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;



const WatchlistContentView = () => {
  const { watchlistId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [watchlist, setWatchlist] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (watchlistId && user) {
      loadWatchlistData();
    }
  }, [watchlistId, user]);

  const loadWatchlistData = async () => {
    setLoading(true);
    try {
      const client = generateClient();
      
      // Load watchlist details
      const watchlistResult = await client.graphql({
        query: getUserWatchlist,
        variables: { id: watchlistId }
      });
      
      setWatchlist(watchlistResult.data.getUserWatchlist);
      
      // Load watchlist items
      const itemsResult = await client.graphql({
        query: GET_WATCHLIST_ITEMS,
        variables: {
          watchlistID: watchlistId,
          limit: 100,
          sortDirection: 'DESC'
        }
      });
      
      const watchlistItems = itemsResult.data.watchlistItemsByWatchlistIDAndCreatedAt?.items || [];
      
      // Fetch content details for each item from MongoDB API
      const itemsWithContent = await Promise.all(
        watchlistItems.map(async (item) => {
          if (item.movieID && item.contentType === 'MOVIE') {
            try {
              const movieData = await getMovieById(item.movieID);
              return {
                ...item,
                movie: movieData
              };
            } catch (error) {
              console.error(`Error fetching movie ${item.movieID}:`, error);
              return {
                ...item,
                movie: {
                  id: item.movieID,
                  name: 'Movie Title Unavailable',
                  poster: '/placeholder-movie.jpg',
                  genre: 'Unknown',
                  rating: 0
                }
              };
            }
          } else if (item.showID && item.contentType === 'SHOW') {
            try {
              const showData = await getShowById(item.showID);
              return {
                ...item,
                show: showData
              };
            } catch (error) {
              console.error(`Error fetching show ${item.showID}:`, error);
              return {
                ...item,
                show: {
                  id: item.showID,
                  name: 'Show Title Unavailable',
                  poster: '/placeholder-movie.jpg',
                  genre: 'Unknown',
                  rating: 0
                }
              };
            }
          }
          return item;
        })
      );
      
      setItems(itemsWithContent);
    } catch (error) {
      console.error('Error loading watchlist:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleContentClick = (item) => {
    if (item.contentType === 'MOVIE' && item.movieID) {
      navigate(`/movies/${item.movieID}`);
    } else if (item.contentType === 'SHOW' && item.showID) {
      navigate(`/shows/${item.showID}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!watchlist) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          Watchlist not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {watchlist.name}
        </Typography>
        {watchlist.description && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {watchlist.description}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {watchlist.isDefault && (
            <Chip label="Default" color="primary" />
          )}
          <Chip 
            label={watchlist.isPublic ? 'Public' : 'Private'} 
            icon={watchlist.isPublic ? <Movie /> : <Tv />}
          />
          <Chip label={`${items.length} items`} />
        </Box>
      </Box>

      {/* Content Grid */}
      {items.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No items in this watchlist
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start adding movies and shows to see them here
          </Typography>
        </Box>
      ) : (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)', 
            md: 'repeat(4, 1fr)',
            lg: 'repeat(5, 1fr)',
            xl: 'repeat(6, 1fr)'
          },
          gap: 2
        }}>
          {items.map((item) => {
            const content = item.movie || item.show;
            const isMovie = item.contentType === 'MOVIE';
            
            return (
              <Box key={item.id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => handleContentClick(item)}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="320"
                      image={content?.posterPath || content?.poster_path || content?.poster || '/placeholder-movie.jpg'}
                      alt={content?.name || 'Content'}
                      sx={{ 
                        objectFit: 'cover',
                        width: '100%',
                        aspectRatio: '2/3'
                      }}
                    />
                    
                    {/* Content Type Badge */}
                    <Chip
                      icon={isMovie ? <Movie /> : <Tv />}
                      label={isMovie ? 'Movie' : 'Show'}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: 'white'
                      }}
                    />
                    
                    {/* Play Button Overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        '.MuiCard-root:hover &': {
                          opacity: 1
                        }
                      }}
                    >
                      <IconButton
                        sx={{
                          bgcolor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(0,0,0,0.9)'
                          }
                        }}
                      >
                        <PlayArrow fontSize="large" />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography 
                      variant="subtitle2" 
                      noWrap 
                      title={content?.name || 'Loading...'}
                      sx={{ fontWeight: 'bold', mb: 0.5 }}
                    >
                      {content?.name || 'Loading...'}
                    </Typography>
                    
                    {content && (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Rating
                            value={content.rating / 2 || 0}
                            precision={0.1}
                            size="small"
                            readOnly
                          />
                          <Typography variant="caption" color="text.secondary">
                            {content.rating?.toFixed(1) || 'N/A'}
                          </Typography>
                        </Box>
                        
                        {content.hypeRating && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Star fontSize="small" color="warning" />
                            <Typography variant="caption" color="primary">
                              Hype: {content.hypeRating.toFixed(1)}
                            </Typography>
                          </Box>
                        )}
                        
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {content.genre}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default WatchlistContentView;